import { Injectable } from '@angular/core';
import { AngularCsv } from 'angular7-csv';
import * as moment from 'moment';
import { from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { LeadsComunicationService } from '../../../pages/leads/leads-comunication.service';
import { LeadOnTrackStatusType } from '../../../shared/models/types/leadOnTrackStatus.type';
import { PaginationInput } from '../../models/common/paginationInput';
import { GetLeadsInput } from '../../models/lead/getLeadsInput';
import { Lead } from '../../models/lead/lead';
import { EventService } from '../event/event.service';
import { LeadService } from '../lead/lead.service';
import { TranslatorService } from '../utils/translator.service';

@Injectable()
export class CsvGeneratorService {

  // TODO This class needs tests and refactor (extract parts to separated classes)

  downloadInProgress = false;
  leadOnTrackOptions;

  private readonly FORMAT_DATE = 'D/M/YYYY HH:mm:ss';
  private readonly ITEMS_PER_PAGE = 50;
  constructor(private readonly translatorService: TranslatorService,
    private readonly leadComuService: LeadsComunicationService,
    private readonly eventService: EventService,
    private readonly leadService: LeadService) {
    this.leadOnTrackOptions = Object.entries(new LeadOnTrackStatusType());
    this.leadOnTrackOptions = this.leadOnTrackOptions.map(leadOnTrackOption => {
      return { type: leadOnTrackOption[1], trans: this.translatorService.trans(`pages.leads.status.${leadOnTrackOption[1]}`) };
    });

  }

  generateCsv(data: object[], filename: string, options: object): AngularCsv {
    return new AngularCsv(data, filename, options);
  }

  async downloadCsv(totalLeads: number, leadInput: GetLeadsInput): Promise<any> {
    this.downloadInProgress = true;
    const options = {
      fieldSeparator: ';',
      showLabels: true,
      headers: this.generateHeaderCsv()
    };

    let leadsCsv = [];
    // const inputForCsv = new GetLeadsInput(new PaginationInput(null, null), leadInput.filter);

    const pagesDownload = Math.ceil(totalLeads / this.ITEMS_PER_PAGE);
    const paginationArray = [];
    for (let i = 0; i < pagesDownload; i++) {
      const inputForCsv = new GetLeadsInput(new PaginationInput(null, null), leadInput.filter);
      inputForCsv.paginationInput.itemPerPage = this.ITEMS_PER_PAGE;
      inputForCsv.paginationInput.page = i;
      inputForCsv.orderBy = leadInput.orderBy;
      inputForCsv.isDownloadCsv = true;
      paginationArray.push(inputForCsv);
    }
    let progress = 1;
    this.eventService.broadcast('downloadStep', { totalPage: pagesDownload, progress });
    from(paginationArray).pipe(
      concatMap(pagination => this.leadService.getLeads$(pagination, true))
    ).subscribe(resultPagintaion => {
      const leadsTmp = this.mapLeadToCsv(resultPagintaion.leads);
      leadsCsv = leadsCsv.concat(leadsTmp);
      progress += 1;
      this.eventService.broadcast('downloadStep', { totalPage: pagesDownload, progress });
    }, () => { }, () => this.launchDownloadFile(leadsCsv, options));
  }

  private launchDownloadFile(leadsCsv, options): void {
    const date = new Date();
    const datestring = date.toLocaleDateString('es-ES');
    const timestring = date.toLocaleTimeString('es-ES');
    this.generateCsv(leadsCsv, `Leads_${datestring}_${timestring}`, options);
    this.downloadInProgress = false;
    this.eventService.broadcast('downloadFinished', {});
  }

  private mapLeadToCsv(leads: Lead[]): any[] {
    return leads.map(lead => {

      const noReportDate = lead.pendingVisitDate ? moment.unix(lead.pendingVisitDate / 1000).format(this.FORMAT_DATE) : '' as string;
      const notComeToDate = lead.failVisitDate ? moment.unix(lead.failVisitDate / 1000).format(this.FORMAT_DATE) : '' as string;
      const yesComeToDate = lead.visitDate ? moment.unix(lead.visitDate / 1000).format(this.FORMAT_DATE) : '' as string;
      const reminder = lead.reminderDate ? moment.unix(lead.reminderDate / 1000).format(this.FORMAT_DATE) : '' as string;

      const tags = lead.tags
        .map(tag => this.translatorService.trans(`pages.leads.tags.${tag}`))
        .join(' | ');

      return new Object({
        contactName: lead.contactName || '' as string,
        contactPhone: lead.contactPhone || '' as string,
        contactEmail: lead.contactEmail || '' as string,
        vat: lead.vat || '' as string,
        user: lead.user ? `${lead.user.name || ''} ${lead.user.lastName || ''}` : '' as string,
        status: this.translatorService.trans(`pages.leads.status.${lead.status}`),
        channel: lead.channel ? this.leadComuService.channels.find(channel => channel.id === lead.channel.toString()).name : '' as string,
        origin: lead.origin || '' as string,
        type: this.translatorService.trans(`pages.leads.type.${lead.type}`),
        brand: lead.brand ? this.leadComuService.brands.find(brand => brand.id === lead.brand.toString()).name : '' as string,
        leadName: lead.descriptionModel || '' as string,
        dealer: this.getName(lead),
        postalCode: `${lead.postalCode} ${lead.location}`.trim(),
        address: lead.address || '' as string,
        entry: lead.createdAt ? moment.unix(lead.createdAt / 1000).format(this.FORMAT_DATE) : '' as string,
        updated: lead.updatedAt ? moment.unix(lead.updatedAt / 1000).format(this.FORMAT_DATE) : '' as string,
        reminder,
        noReportDate,
        notComeToDate,
        yesComeToDate,
        endDefeatDate: lead.endDefeatDate ? moment.unix(lead.endDefeatDate / 1000).format(this.FORMAT_DATE) : '' as string,
        scoring: lead.scoring === 0 || lead.scoring ? lead.scoring : '' as string,
        comment: lead.comments || '' as string,
        tag: tags,
        plate: lead.plate || '' as string,
        temp: lead.onTrack ? this.leadOnTrackOptions.find(onTrackOptions => onTrackOptions.type === lead.onTrack).trans : '',
        leadId: lead.id
      });

    });
  }

  private getName(lead: Lead) {
    if (lead.dealer) {
      const dealerFound = this.leadComuService.dealers.find(dealer => dealer.id === lead.dealer.toString());
      if (dealerFound) {
        return dealerFound.name;
      }
    }
    return '' as string;
  }

  private generateHeaderCsv(): string[] {
    const contactName = this.translatorService.trans('pages.leads.name');
    const contactPhone = this.translatorService.trans('pages.leads.phone');
    const contactEmail = this.translatorService.trans('pages.leads.email');
    const vat = this.translatorService.trans('pages.leads.vat');
    const user = this.translatorService.trans('pages.leads.user');
    const status = this.translatorService.trans('pages.leads.status');
    const channel = this.translatorService.trans('pages.leads.channel');
    const origin = this.translatorService.trans('pages.leads.origin');
    const type = this.translatorService.trans('pages.leads.type');
    const leadName = this.translatorService.trans('pages.leads.description_model');
    const brand = this.translatorService.trans('pages.leads.brand');
    const dealer = this.translatorService.trans('pages.leads.dealer');
    const postalCode = this.translatorService.trans('pages.leads.postal_code');
    const address = this.translatorService.trans('pages.leads.address');
    const entry = this.translatorService.trans('pages.leads.created_at');
    const modified = this.translatorService.trans('pages.leads.updated_at');
    const endDefeatDate = this.translatorService.trans('pages.leads.tags.expiration');
    const reminder = this.translatorService.trans('pages.leads.reminder');
    const noReport = this.translatorService.trans('filters.no_report');
    const notCome = this.translatorService.trans('filters.not_come');
    const yesCome = this.translatorService.trans('filters.yes_come');
    const comment = this.translatorService.trans('pages.leads.comments');
    const scoring = this.translatorService.trans('pages.leads.scoring');
    const tag = this.translatorService.trans('pages.leads.tag');
    const plate = this.translatorService.trans('pages.leads.plate');
    const temp = this.translatorService.trans('template.import.header.temp');
    const leadId = this.translatorService.trans('template.import.header.id');

    const headers = [
      contactName,
      contactPhone,
      contactEmail,
      vat,
      user,
      status,
      channel,
      origin,
      type,
      brand,
      leadName,
      dealer,
      postalCode,
      address,
      entry,
      modified,
      reminder,
      noReport,
      notCome,
      yesCome,
      endDefeatDate,
      scoring,
      comment,
      tag,
      plate,
      temp,
      leadId
    ];

    return headers;
  }

}
